/**
 * A tenant that moved to Jamaat360 is read-only on this app (the API lists it in READ_ONLY_TENANT_IDS).
 * `GET /tenant-branding/me` returns `retirement`, and a refused write returns code TENANT_RETIRED.
 */

export const TENANT_RETIRED_CODE = "TENANT_RETIRED";
export const TENANT_RETIREMENT_EVENT = "fakhri-tenant-retirement-updated";
export const MOVED_NOTICE_PATH = "/moved";

const RETIREMENT_STORAGE_KEY = "tenantRetirement";
const VIEW_OLD_APP_STORAGE_KEY = "tenantRetirementViewOldApp";

export type TenantRetirement = {
  mode: "readonly";
  newAppUrl: string;
  /** True only for a platform (super) admin, who may still look at the old records. */
  canViewOldData: boolean;
};

export type RetirementGateAction = "none" | "redirect-to-notice" | "banner";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/** Parses the `retirement` field of the branding row; anything malformed counts as "not retired". */
export function parseTenantRetirement(value: unknown): TenantRetirement | null {
  if (!isRecord(value) || value.mode !== "readonly" || !isHttpsUrl(value.newAppUrl)) return null;
  return {
    mode: "readonly",
    newAppUrl: value.newAppUrl,
    canViewOldData: value.canViewOldData === true,
  };
}

/** The refused-write body is `{ message: { code, newAppUrl } }` (the API filter wraps the exception response). */
export function retirementFromHttpError(error: unknown): { newAppUrl: string } | null {
  if (!isRecord(error)) return null;
  for (const body of [error.body, error.json]) {
    const message = isRecord(body) ? body.message : undefined;
    if (
      isRecord(message) &&
      message.code === TENANT_RETIRED_CODE &&
      isHttpsUrl(message.newAppUrl)
    ) {
      return { newAppUrl: message.newAppUrl };
    }
  }
  return null;
}

export function readStoredTenantRetirement(): TenantRetirement | null {
  try {
    const raw = sessionStorage.getItem(RETIREMENT_STORAGE_KEY);
    return raw ? parseTenantRetirement(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function storeTenantRetirement(retirement: TenantRetirement | null): void {
  try {
    if (retirement) {
      sessionStorage.setItem(RETIREMENT_STORAGE_KEY, JSON.stringify(retirement));
    } else {
      sessionStorage.removeItem(RETIREMENT_STORAGE_KEY);
      sessionStorage.removeItem(VIEW_OLD_APP_STORAGE_KEY);
    }
  } catch {
    /* storage unavailable: the gate then relies on the next API answer */
  }
  window.dispatchEvent(new Event(TENANT_RETIREMENT_EVENT));
}

/** A refused write proves the tenant is retired even when the cached branding predates the switch. */
export function noteRetirementFromHttpError(error: unknown): void {
  const found = retirementFromHttpError(error);
  if (!found || readStoredTenantRetirement()) return;
  storeTenantRetirement({ mode: "readonly", newAppUrl: found.newAppUrl, canViewOldData: false });
}

export function hasChosenToViewOldApp(): boolean {
  try {
    return sessionStorage.getItem(VIEW_OLD_APP_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Only honoured for a platform admin. */
export function chooseToViewOldApp(): void {
  const retirement = readStoredTenantRetirement();
  if (!retirement?.canViewOldData) return;
  try {
    sessionStorage.setItem(VIEW_OLD_APP_STORAGE_KEY, "1");
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(TENANT_RETIREMENT_EVENT));
}

/** Where a signed-in user must go: the notice screen for everyone except a platform admin who chose the old app. */
export function getRetirementGateAction(
  retirement: TenantRetirement | null,
  viewOldAppChosen: boolean,
  pathname: string
): RetirementGateAction {
  if (!retirement) return "none";
  if (retirement.canViewOldData && viewOldAppChosen) return "banner";
  return pathname === MOVED_NOTICE_PATH ? "none" : "redirect-to-notice";
}

/** Route after a successful sign-in. */
export function postLoginRedirectPath(retirement: TenantRetirement | null): string {
  return retirement ? MOVED_NOTICE_PATH : "/";
}
